import { BackdropProvider, Notification } from "@rothoblaas/design-system";
import { ComponentType, FC } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import AuthProvider, { RothoSSOSettings } from "../context/authContext";
import NotificationDispatch, { hideNotification, useNotificationContext } from "../context/notificationContext";
import { AppProvider } from "../context/appContext";
import { ACCESS_SCOPE_MAIN_APP, AccessScope } from "../http/types/accessControl";
import ManageProjectInvite from "../pages/projects/ManageProjectInvite";
import ManageCalculationInvite from "../pages/calculations/ManageCalculationInvite";
import AccessGate from "./AccessGate";

type AppProps = {
  AuthenticatedApp: ComponentType;
  Logo: ComponentType;
  basename?: string;
  isSingleModuleApp?: boolean;
  scope?: AccessScope;
};

const App: FC<AppProps> = ({
  AuthenticatedApp,
  Logo,
  basename,
  isSingleModuleApp = false,
  scope = ACCESS_SCOPE_MAIN_APP,
}) => {
  const settings: RothoSSOSettings = {
    clientId: import.meta.env.VITE_CLIENT_ID,
    iamUrl: import.meta.env.VITE_IAM_URL,
  };

  return (
    <BrowserRouter basename={basename}>
      <BackdropProvider>
        <AppProvider isSingleModuleApp={isSingleModuleApp} scope={scope} Logo={Logo}>
          <AuthProvider settings={settings}>
            <AppContent AuthenticatedApp={AuthenticatedApp} />
          </AuthProvider>
        </AppProvider>
      </BackdropProvider>
    </BrowserRouter>
  );
};

const AppContent: FC<Pick<AppProps, "AuthenticatedApp">> = ({ AuthenticatedApp }) => {
  const [{ id, show, severity, message, sticky }, dispatch] = useNotificationContext();

  // Every route sits behind the AccessGate, so a user who is blocked from, or locked out of, this
  // app sees the access-denied page and nothing else. The invite pages also work anonymously, so
  // they don't force a login: there the gate only steps in once an identity is known and refused.
  return (
    <NotificationDispatch.Provider value={dispatch}>
      <Routes>
        <Route
          path="/invite/:token"
          element={
            <AccessGate>
              <ManageProjectInvite />
            </AccessGate>
          }
        />
        <Route
          path="/calculation-invite/:token"
          element={
            <AccessGate>
              <ManageCalculationInvite />
            </AccessGate>
          }
        />
        <Route
          path="/*"
          element={
            <AccessGate requireLogin>
              <AuthenticatedApp />
            </AccessGate>
          }
        />
      </Routes>
      {show && (
        <Notification
          key={id}
          type={severity}
          text={message ?? ""}
          sticky={sticky}
          onClose={() => dispatch(hideNotification())}
        />
      )}
    </NotificationDispatch.Provider>
  );
};

export default App;
